import React from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import { UnlinkedBatchModalProps } from '@v2/pages/planboard/types';

import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import { SCREEN_BORDER_RADIUS, PLANBOARD_TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import useJobStatusBadgeConfig from '@v2/pages/planboard/hooks/useJobStatusBadgeConfig';
import useElapsedTime from '@v2/pages/planboard/hooks/useElapsedTime';
import ModalWrapper from '@components/modal/ModalWrapper';
import JobCardInfoModalRow from './JobCardInfoModalRow';
import { useCalculateModalPosition } from '@v2/pages/planboard/hooks/useCalculateModalPosition';
import StatusBadge from '@components/common/StatusBadge';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import ModalArrowPointer from '@v2/pages/planboard/components/JobCardPreview/ModalArrowPointer';

const UnlinkedBatchModal: React.FC<UnlinkedBatchModalProps> = ({ isOpen, onClose, item, anchorRef, onLinkBatch, C, scale, font }) => {
  const position = useCalculateModalPosition({ isOpen, anchorRef, scale });
  const jobStatusConfig = useJobStatusBadgeConfig({ C, status: item.status });
  const { elapsedTimeValue, shouldRunTime } = useElapsedTime(item);

  // Get batch number from linkedNcFiles if available
  const batchNumber = item.linkedNcFiles?.find((nc) => nc.productionBatchNumber != null)?.productionBatchNumber;

  const handleLinkBatchPress = () => {
    onClose();
    onLinkBatch();
  };

  const modalWidth = scale(PLANBOARD_TILE_CONFIG.MODAL_WIDTH);

  return (
    <ModalWrapper isVisible={isOpen} C={C} onBackdropPress={onClose} backdropColor="transparent" backdropOpacity={0}>
      <ModalArrowPointer
        position={position}
        scale={scale}
        C={C}
        font={font}
        width={modalWidth}
        maxHeight={position.maxHeight}
        contentContainerStyle={{
          backgroundColor: C('Background/Light/Panel [mobile]'),
          padding: scale(15),
        }}
      >
        {/* Main Info Section */}
        <View
          style={{
            backgroundColor: C('Background/Light/Field on Panel'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            paddingHorizontal: scale(16),
          }}
        >
          {/* Batch Row */}
          <JobCardInfoModalRow
            label={i18n.t('Batch')}
            value={
              batchNumber ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(4) }}>
                  <Text style={[font['Body'], { color: C('Link/Light/Primary') }]}>{batchNumber}</Text>
                  <Svg icon="arrowLight" width={scale(24)} height={scale(24)} color="Link/Light/Primary" />
                </View>
              ) : (
                <Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Unknown')}</Text>
              )
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Status Row */}
          <JobCardInfoModalRow
            label={i18n.t('Status')}
            value={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(6) }}>
                <StatusBadge C={C} config={jobStatusConfig} scale={scale} font={font} />
              </View>
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Batch Link Row - Clickable */}
          <TouchableElement onPress={handleLinkBatchPress}>
            {({ pressHoverColor, boxStyle }) => (
              <View
                style={[
                  boxStyle,
                  {
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingVertical: scale(10),
                    borderBottomWidth: 1,
                    borderBottomColor: C('Hairline/Light/No trans [mobile]'),
                    backgroundColor: C(pressHoverColor('Background/Light/Field on Panel')),
                  },
                ]}
              >
                <Text style={[font['Body'], { color: C('Label/Light/Primary') }]}>{i18n.t('Batch Link')}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(6) }}>
                  <Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Not Linked')}</Text>
                  <Svg icon="notLinked" width={scale(20)} height={scale(20)} />
                  <Svg icon="chevronRight" width={scale(8)} height={scale(14)} color="Label/Light/Tertiary" />
                </View>
              </View>
            )}
          </TouchableElement>

          {/* Ready Row */}
          <JobCardInfoModalRow
            label={i18n.t('Ready')}
            value={<Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>{item.quantityProduced ?? 0} Pcs</Text>}
            isLast
            C={C}
            scale={scale}
            font={font}
          />
        </View>

        {/* Times Label - outside white background */}
        <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary'), marginTop: scale(12), marginBottom: scale(8), marginLeft: scale(16) }]}>
          {i18n.t('TIMES')}
        </Text>

        {/* Times Section */}
        <View
          style={{
            backgroundColor: C('Background/Light/Field on Panel'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            paddingHorizontal: scale(16),
          }}
        >
          {/* Elapsed Time Row */}
          <JobCardInfoModalRow
            label={i18n.t('Elapsed Time')}
            value={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(4) }}>
                <Svg icon="clock" width={scale(16)} height={scale(16)} color="System/Light/Blue" />
                <TimeDisplayInStaticFormat
                  timeValue={elapsedTimeValue}
                  shouldRunTime={shouldRunTime}
                  withHMSFormat={false}
                  C={C}
                  scale={scale}
                  font={font}
                  fontForDigit="Body"
                  fontForSecondDigit="Body"
                  fontForLetter="Body"
                  fontForSecondLetter="Body"
                  includeSecondsAlways={false}
                  color="Label/Light/Tertiary"
                />
              </View>
            }
            isLast
            C={C}
            scale={scale}
            font={font}
          />
        </View>
      </ModalArrowPointer>
    </ModalWrapper>
  );
};

export default ColorWrapper(UnlinkedBatchModal);
